import { useQueryClient } from '@tanstack/react-query';
import type { Hex } from 'viem';
import type { Address, EthFaucetClaim } from '../../shared/types';
import { fmtEth } from '../../shared/units';
import { explainError } from '../lib/errors';
import { keys, publicClient, useRefreshAfterTx } from './queries';
import { useTx, type TxResult } from './tx';

export type EthClaimPhase = 'idle' | 'requesting' | 'pending';
type ClaimError = { error?: string; code?: string; retryAt?: number };

/**
 * Asks the Worker for the ETH drip (it verifies the human check, applies the policy and sends from its own key),
 * waits for the transfer to land, then refreshes the wallet's numbers. Never throws: failures come back as
 * { ok: false, error } and as a toast, like the transaction helper.
 */
export function useEthClaim() {
  const { notify } = useTx();
  const refresh = useRefreshAfterTx();
  const qc = useQueryClient();
  return async (address: Address, token: string, onPhase?: (p: EthClaimPhase) => void): Promise<TxResult> => {
    const fail = (error: string): TxResult => {
      notify({ kind: 'error', title: 'ETH faucet failed', body: error }, 12_000);
      onPhase?.('idle');
      return { ok: false, error };
    };
    onPhase?.('requesting');
    let body: Partial<EthFaucetClaim> & ClaimError = {};
    let status = 0;
    try {
      const res = await fetch('/api/faucet/eth', { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify({ address, token }) });
      status = res.status;
      body = (await res.json()) as typeof body;
    } catch {
      return fail('The faucet is unreachable. Try again in a minute.');
    }
    void qc.invalidateQueries({ queryKey: keys.ethFaucet(address) });
    if (status !== 200 || !body.tx) return fail(body.error ?? `The faucet answered with status ${status}.`);
    const hash = body.tx as Hex;
    const amount = `${fmtEth(BigInt(body.amountWei ?? '0'))} ETH`;
    onPhase?.('pending');
    notify({ kind: 'pending', title: 'ETH on the way', body: `Faucet: ${amount}`, hash });
    try {
      const receipt = await publicClient.waitForTransactionReceipt({ hash, timeout: 90_000, pollingInterval: 1_000 });
      if (receipt.status !== 'success') throw new Error('The faucet transaction reverted on-chain.');
    } catch (e) {
      return fail(explainError(e));
    }
    await refresh(address);
    notify({ kind: 'success', title: `${amount} received`, hash }, 8_000);
    onPhase?.('idle');
    return { ok: true, hash };
  };
}
